import { Link, useLocation } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Calendar, FileText, UserCog, Settings, LogOut, ExternalLink,
} from "lucide-react";
import HirafiIcon from "@/components/HirafiIcon";
import { useAuth } from "@/contexts/AuthContext";
import { usePendingAppointmentsCount } from "@/hooks/usePendingAppointmentsCount";
import { useLocalizedPath } from "@/hooks/useLocalizedNavigate";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ArtisanDrawerMenu = ({ open, onOpenChange }: Props) => {
  const { signOut, user } = useAuth();
  const location = useLocation();
  const pendingRdv = usePendingAppointmentsCount();
  const lPath = useLocalizedPath();

  const close = () => onOpenChange(false);

  const isActive = (path: string) => location.pathname === path;

  const activityItems = [
    {
      to: lPath("/mes-rendez-vous"),
      icon: Calendar,
      label: "Mes rendez-vous",
      badge: pendingRdv > 0 ? pendingRdv : undefined,
    },
    {
      to: lPath("/mes-devis"),
      icon: FileText,
      label: "Mes devis",
      badge: undefined,
    },
  ];

  const settingsItems = [
    { to: lPath("/mon-profil-artisan"), icon: UserCog, label: "Mon profil" },
    { to: lPath("/parametres"), icon: Settings, label: "Paramètres" },
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[280px] p-0 flex flex-col" role="dialog" aria-label="Menu artisan">
        {/* Header */}
        <SheetHeader className="px-5 pt-6 pb-4 border-b">
          <SheetTitle className="text-left font-display flex items-center gap-2.5">
            <HirafiIcon size={32} />
            <span>Menu</span>
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          {/* Activity section */}
          <div className="px-3 py-3">
            <p className="px-2 mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Activité</p>
            <div className="space-y-0.5">
              {activityItems.map((item) => (
                <Link key={item.to} to={item.to} onClick={close}>
                  <Button
                    variant={isActive(item.to) ? "secondary" : "ghost"}
                    className={cn(
                      "w-full justify-start gap-3 h-11 font-medium",
                      isActive(item.to) && "text-primary"
                    )}
                    aria-current={isActive(item.to) ? "page" : undefined}
                  >
                    <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                    <span className="flex-1 text-left text-sm">{item.label}</span>
                    {item.badge && (
                      <Badge className="h-5 min-w-5 rounded-full bg-warning px-1.5 text-[10px] text-warning-foreground leading-none">
                        {item.badge}
                      </Badge>
                    )}
                  </Button>
                </Link>
              ))}
            </div>
          </div>

          <Separator />

          {/* Settings section */}
          <div className="px-3 py-3">
            <p className="px-2 mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Compte</p>
            <div className="space-y-0.5">
              {settingsItems.map((item) => (
                <Link key={item.to} to={item.to} onClick={close}>
                  <Button
                    variant={isActive(item.to) ? "secondary" : "ghost"}
                    className={cn(
                      "w-full justify-start gap-3 h-11 font-medium",
                      isActive(item.to) && "text-primary"
                    )}
                    aria-current={isActive(item.to) ? "page" : undefined}
                  >
                    <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                    <span className="text-sm">{item.label}</span>
                  </Button>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="border-t px-3 py-3 space-y-0.5">
          <Link to={lPath("/")} onClick={close}>
            <Button variant="ghost" className="w-full justify-start gap-3 h-11 text-muted-foreground text-sm font-medium">
              <ExternalLink className="h-4 w-4 shrink-0" aria-hidden="true" />
              Voir le site public
            </Button>
          </Link>
          <Button
            variant="ghost"
            className="w-full justify-start gap-3 h-11 text-destructive hover:text-destructive hover:bg-destructive/8 text-sm font-medium"
            onClick={() => { signOut(); close(); }}
          >
            <LogOut className="h-4 w-4 shrink-0" aria-hidden="true" />
            Déconnexion
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ArtisanDrawerMenu;
